import React from 'react';
import { Check, Zap, TrendingUp, Flame, Clock } from 'lucide-react';
import { formatDuration, getLocalDateStr, getSchichtForDate } from '../../lib/habitUtils';

const SCHICHT_BADGE = {
  Früh:  'bg-amber-100 text-amber-700',
  Spät:  'bg-blue-100 text-blue-700',
  Nacht: 'bg-violet-100 text-violet-700',
  Frei:  'bg-slate-100 text-slate-500',
};

export default function HabitCard({ habit, log, streak, onToggle, onPerformanceClick, onDetailClick }) {
  const isDone = log?.done || false;
  const isBinary = habit.type === 'binary';
  const isPerformance = habit.type === 'performance';

  // Schicht badge for today
  const schichtHeute = habit.schedule?.frequencyType === 'schichten'
    ? (getSchichtForDate(habit, getLocalDateStr()) ?? 'Frei')
    : null;

  const statusColors = {
    new: 'border-l-emerald-500',
    maintenance: 'border-l-blue-500',
    paused: 'border-l-slate-300'
  };

  return (
    <div
      className={`bg-white rounded-xl border-l-4 ${statusColors[habit.status] || 'border-l-slate-300'} shadow-sm hover:shadow-md transition-all ${isDone ? 'opacity-70' : ''}`}
      data-testid={`habit-card-${habit.id}`}
    >
      <div className="flex items-center gap-3 p-3">
        {/* Check button */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            if (isBinary) {
              onToggle(habit);
            } else {
              isDone ? onToggle(habit) : onPerformanceClick(habit);
            }
          }}
          className={`w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0 transition-all ${
            isDone
              ? 'bg-emerald-500 text-white shadow-md shadow-emerald-200'
              : 'border-2 border-slate-300 hover:border-emerald-400 hover:bg-emerald-50 text-slate-400'
          }`}
          data-testid={`habit-check-${habit.id}`}
        >
          {isDone ? <Check className="w-5 h-5" /> : (isPerformance ? <Zap className="w-5 h-5" /> : null)}
        </button>

        {/* Content */}
        <div className="flex-1 min-w-0 cursor-pointer" onClick={() => onDetailClick(habit)}>
          <div className="flex items-center gap-2 flex-wrap">
            <p className={`text-sm font-medium ${isDone ? 'line-through text-slate-400' : 'text-slate-800'}`}>
              {habit.title}
            </p>
            {habit.status === 'new' && (
              <span className="px-1.5 py-0.5 bg-emerald-100 text-emerald-700 text-[10px] font-bold rounded">NEU</span>
            )}
            {schichtHeute && (
              <span className={`px-1.5 py-0.5 text-[10px] font-semibold rounded ${SCHICHT_BADGE[schichtHeute] || 'bg-slate-100 text-slate-500'}`}>
                {schichtHeute}schicht
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 mt-0.5">
            {habit.ifThen?.cueCustom && (
              <span className="text-xs text-slate-400">{habit.ifThen.cueCustom}</span>
            )}
            {isPerformance && log?.value && (
              <span className="text-xs text-blue-600 font-medium">{log.value} {habit.tracking?.unitLabel}</span>
            )}
            {isPerformance && !isDone && habit.tracking?.currentTarget && (
              <span className="text-xs text-slate-400">Ziel: {habit.tracking.currentTarget} {habit.tracking?.unitLabel}</span>
            )}
          </div>
        </div>

        {/* Streak / Status / Duration */}
        <div className="flex flex-col items-end gap-0.5 flex-shrink-0">
          {streak > 0 && (
            <div className="flex items-center gap-0.5">
              <Flame className={`w-3.5 h-3.5 ${streak >= 7 ? 'text-orange-500' : 'text-slate-400'}`} />
              <span className={`text-xs font-bold ${streak >= 7 ? 'text-orange-500' : 'text-slate-400'}`}>{streak}</span>
            </div>
          )}
          {isPerformance && habit.tracking?.goalTarget && (
            <div className="flex items-center gap-0.5">
              <TrendingUp className="w-3 h-3 text-blue-400" />
              <span className="text-[10px] text-blue-400">{habit.tracking.currentTarget}/{habit.tracking.goalTarget}</span>
            </div>
          )}
          {habit.startDate && (
            <div className="flex items-center gap-0.5 mt-0.5">
              <Clock className="w-3 h-3 text-slate-300" />
              <span className="text-[10px] text-slate-300 font-medium">{formatDuration(habit.startDate)}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

